import React, { useState } from "react";
import { formatDate } from "../lib/utils";
import {
  Address,
  Message,
  moveToInboxAndTagAsDonated,
  runCobutlerFiltering,
  testRules,
} from "../lib/messagesLib";
import MessageRowDetail from "./messageRowDetail";
import { createChallenge } from "../lib/challengeLib";
import Dropdown from "./Dropdown";
import { CreateRuleDTO, RulePatternTarget, createRule } from "../lib/rulesLib";

interface AddressesCellProps {
  message: Message;
}

const AddressesCell: React.FC<AddressesCellProps> = ({ message }) => {
  const renderRecipients = (type: string, recipients: Address[]) => {
    if (recipients.length === 0) return null;

    return (
      <div className="max-w-[300px] overflow-hidden">
        <strong>
          {type}
          {recipients.length > 1 ? `[${recipients.length}]` : ""}:{" "}
        </strong>
        {recipients.map((recipient, index) => (
          <span key={index}>
            {recipient.name ? `${recipient.name} ` : ""}
            &lt;{recipient.address}&gt;
            {index < recipients.length - 1 && ", "}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-[300px] overflow-hidden">
      {renderRecipients("from", message.from)}
      {renderRecipients("to", message.to)}
      {renderRecipients("cc", message.cc)}
      {renderRecipients("bcc", message.bcc)}
    </div>
  );
};
interface TagProps {
  text: string;
}

const Tag: React.FC<TagProps> = ({ text }) => {
  return (
    <span className="bg-blue-500 text-white text-[0.5rem] font-medium me-1 px-1 py-0 rounded dark:bg-blue-900 dark:text-white-300">
      {text}
    </span>
  );
};
interface MessageRowProps {
  message: Message;
  labelMap: { id: string; name: string }[];
  onMessageUpdate?: () => void;
}

const MessageRow: React.FC<MessageRowProps> = ({
  message,
  labelMap,
  onMessageUpdate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [subjectPattern, setSubjectPattern] = useState("");
  const [useSubjectSender, setUseSubjectSender] = useState(false);

  const handleExpandToggle = () => setExpanded(!expanded);

  const getLabelName = function (labelId: string) {
    if (!labelMap) return labelId;
    for (let i = 0; i <= labelMap.length; i++) {
      if (labelMap[i]?.id === labelId) return labelMap[i].name;
    }
    return labelId;
  };

  const handleCreateChallenge = async function (doSend: boolean) {
    console.log(
      `Creating a challenge for message ${message.id} with doSend ${doSend}`
    );
    setIsGenerating(true);
    try {
      await createChallenge(message.id, doSend);
      setIsRefreshing(true);
      if (onMessageUpdate) {
        await onMessageUpdate();
      }
    } finally {
      setIsGenerating(false);
      setIsRefreshing(false);
    }
  };

  const handleMoveToInboxAndTagAsDonated = async function () {
    console.log(`Moving message ${message.id} to inbox and tagging as donated`);
    setIsGenerating(true);
    try {
      await moveToInboxAndTagAsDonated(message.id);
      setIsRefreshing(true);
      if (onMessageUpdate) {
        await onMessageUpdate();
      }
    } finally {
      setIsGenerating(false);
      setIsRefreshing(false);
    }
  };

  const handleEvaluateRules = async function () {
    console.log(`Testing rules against message ${message.id}`);
    await testRules(message.id);
  };

  const handleRunCobutlerFiltering = async function (
    filteringMode: "simulating" | "filtering"
  ) {
    console.log(
      `Running Cobutler filtering for message ${message.id} with mode ${filteringMode}`
    );
    const result = await runCobutlerFiltering(message.id, filteringMode);
    console.log(result);
  };

  const handleQuickAllow = async (type: "sender" | "domain" | "subject") => {
    const rule: CreateRuleDTO = {
      messageId: message.id,
      scopeTargetType: "global",
      scopeTargetId: null,
      ruleAction: "allow",
      extraSourceOption:
        type === "subject" ? (useSubjectSender ? "sender" : "domain") : type,
      category: "",
      selectedHeaders: {},
    };

    if (type === "subject") {
      rule.patterns = [
        {
          target: RulePatternTarget.SUBJECT,
          value: subjectPattern,
        },
      ];
    }

    await createRule(rule);
    if (onMessageUpdate) {
      await onMessageUpdate();
    }
  };

  const isSentOrDraft = message.labels.some(
    (label) => getLabelName(label) === "SENT" || getLabelName(label) === "DRAFT"
  );

  return (
    <>
      <tr className="even:bg-gray-100 dark:even:bg-gray-700">
        <td className="text-nowrap relative">
          <button
            className="hover:text-blue-500"
            onClick={() => navigator.clipboard.writeText(message.id)}
            title="Copy ID"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
          <span className="absolute z-10 bg-gray-800 text-white text-xs rounded p-2 -mt-8 left-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {message.id}
          </span>
        </td>
        <td className="text-nowrap">
          {formatDate(message.timestamp)}
          {/*<br/>{timeAgo(message.timestamp)}*/}
        </td>
        <td className="text-left">
          <AddressesCell key={message.id} message={message} />
        </td>
        <td>
          <div className="flex items-center">
            {message.subject}
            {isSentOrDraft && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-20 w-20 ml-2 text-gray-600 dark:text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76"
                />
              </svg>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {message.labels.map((label: string) => (
                <Tag key={label} text={getLabelName(label)} />
              ))}
            </div>
          </div>
        </td>
        <td>
          {message.challenge && (
            <a
              href={`${process.env.REACT_APP_CHALLENGE_APP_URL}/${message.challenge.token}/donate`}
              target="_blank"
              rel="noreferrer"
            >
              View Challenge
            </a>
          )}
        </td>
        <td className="space-x-2">
          {!isSentOrDraft && (
            <>
              <button
                onClick={() => handleQuickAllow("sender")}
                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                title="Allow sender globally"
              >
                Allow Sender
              </button>
              <button
                onClick={() => handleQuickAllow("domain")}
                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                title="Allow domain globally"
              >
                Allow Domain
              </button>
              <button
                onClick={() => {
                  setSubjectPattern(message.subject);
                  setShowSubjectModal(true);
                }}
                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                title="Allow subject pattern globally"
              >
                Allow Subject
              </button>
            </>
          )}
        </td>
        <td>
          {isGenerating ? (
            <div>Processing...</div>
          ) : isRefreshing ? (
            <div>Refreshing...</div>
          ) : (
            <Dropdown
              trigger={
                <button className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none">
                  Other Actions ▼
                </button>
              }
              items={[
                {
                  label: "Create challenge (don't send)",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleCreateChallenge(false);
                  },
                },
                {
                  label: "Evaluate rules (old)",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleEvaluateRules();
                  },
                },
                {
                  label: "Run Cobutler filtering (simulating)",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleRunCobutlerFiltering("simulating");
                  },
                },
                {
                  label: "Run Cobutler filtering (filtering)",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleRunCobutlerFiltering("filtering");
                  },
                },
                {
                  label: "Create new email filter",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleExpandToggle();
                  },
                },
                {
                  label: "Move to inbox and tag as donated",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleMoveToInboxAndTagAsDonated();
                  },
                },
              ]}
            />
          )}
        </td>
        {/*hiding tooltip for now*/}
        {/*{showTooltip && (*/}
        {/*    <div*/}
        {/*        className="fixed w-200 p-2 bg-gray-800 text-white text-xs rounded shadow-lg tooltip"*/}
        {/*        style={{ top: tooltipPosition.y-300, left: tooltipPosition.x + 10 }}*/}
        {/*    >*/}
        {/*        {message.headers.map((header, index) => (*/}
        {/*            <div key={index} className="flex justify-between mb-1">*/}
        {/*                <div className="text-right pr-2" style={{ width: '250px' }}>{header.name}</div>*/}
        {/*                <div className="text-left pl-2" style={{ width: '100%', textWrap:'nowrap' }}>{header.value}</div>*/}
        {/*            </div>                    ))}*/}
        {/*    </div>*/}
        {/*)}*/}
      </tr>
      {showSubjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">
              Creating allow rule for subject line
            </h3>
            <div className="mb-4">
              <label className="block mb-2">
                <input
                  type="checkbox"
                  checked={useSubjectSender}
                  onChange={(e) => setUseSubjectSender(e.target.checked)}
                  className="mr-2"
                />
                Use sender (unchecked = use domain)
              </label>
            </div>
            <input
              type="text"
              value={subjectPattern}
              onChange={(e) => setSubjectPattern(e.target.value)}
              className="w-full p-2 border rounded mb-4 dark:bg-gray-700"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Will match: <b>{subjectPattern.replace(/\*/g, "{anything}")}</b>
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowSubjectModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleQuickAllow("subject");
                  setShowSubjectModal(false);
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Create Rule
              </button>
            </div>
          </div>
        </div>
      )}
      {expanded && (
        <tr>
          <td colSpan={4} className="p-0">
            <div className="overflow-x-auto relative">
              <button
                onClick={handleExpandToggle}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
              <MessageRowDetail message={message} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default MessageRow;
